/**
 * FREQUENCY SETS - Historical and Experimental
 * 
 * WARNING: Most claims about specific frequencies having healing/psychological
 * properties lack scientific evidence. This file contains:
 * - Historical associations (Solfeggio from medieval chants)
 * - Cultural traditions (chakra associations from Tantric systems)
 * - Unverified claims from alternative medicine literature
 * 
 * DISCLAIMER: No scientific evidence supports claims of "historical association",
 * "healing", or specific psychological effects from these frequencies.
 * 
 * STATUS: Requires research (Week 3 psychoacoustics) to determine what
 * can legitimately be claimed. Currently preserved for reference only.
 * 
 * Citations mentioned (Horowitz, Emoto, etc.) have not been verified
 * and many have been debunked or lack peer-reviewed support.
 * 
 * TODO: After Week 3 research, remove unsubstantiated content or
 * add proper research-backed context.
 */

// ============================================================================
// SOLFEGGIO FREQUENCIES - Historical & Research-Backed
// ============================================================================

/**
 * Ancient Solfeggio Scale
 * 
 * Historical origin: Hymn to St. John the Baptist (11th century)
 * Ut queant laxis, Resonare fibris, Mira gestorum, Famuli tuorum,
 * Solve polluti, Labii reatum, Sancte Iohannes
 * 
 * Rediscovered by Dr. Joseph Puleo in the 1970s using Pythagorean reduction
 * on verses from the Book of Numbers (Chapter 7, verses 12-83)
 * 
 * Mathematical pattern: Each frequency reduces to 3, 6, or 9 (Tesla's key numbers)
 * 396 → 3+9+6 = 18 → 1+8 = 9
 * 417 → 4+1+7 = 12 → 1+2 = 3
 * 528 → 5+2+8 = 15 → 1+5 = 6
 * 639 → 6+3+9 = 18 → 1+8 = 9
 * 741 → 7+4+1 = 12 → 1+2 = 3
 * 852 → 8+5+2 = 15 → 1+5 = 6
 * 
 * Source: Horowitz, L. G. (1999). "Healing Codes for the Biological Apocalypse"
 */
export const SOLFEGGIO_FREQUENCIES = {
  UT_396: {
    frequency: 396,
    noteName: 'Ut',
    originalLatin: 'Ut queant laxis',
    translation: 'That your servants may freely sing',
    intention: 'Liberation from fear and guilt',
    chakra: 'Root (Muladhara)',
    element: 'Earth',
    color: 'Red',
    effects: [
      'Releases emotional patterns',
      'Removes blockages',
      'Liberates from fear',
      'Guilt release'
    ],
    pythagoreanReduction: '3+9+6 = 18 → 1+8 = 9',
    researchNotes: 'Historically used for grounding and releasing negative emotions',
    harmonics: [396, 792, 1188, 1584], // Octaves and harmonics
  },
  
  RE_417: {
    frequency: 417,
    noteName: 'Re',
    originalLatin: 'Resonare fibris',
    translation: 'With the wonder of your miracles',
    intention: 'Undoing situations and facilitating change',
    chakra: 'Sacral (Svadhisthana)',
    element: 'Water',
    color: 'Orange',
    effects: [
      'Facilitates change',
      'Clears traumatic experiences',
      'Energizes cells',
      'Removes negative energy'
    ],
    pythagoreanReduction: '4+1+7 = 12 → 1+2 = 3',
    researchNotes: 'Associated with transformation and breaking negative patterns',
    harmonics: [417, 834, 1251, 1668],
  },
  
  MI_528: {
    frequency: 528,
    noteName: 'Mi',
    originalLatin: 'Mira gestorum',
    translation: 'Sound from your lips',
    intention: 'Transformation (historical association)',
    chakra: 'Solar Plexus (Manipura)',
    element: 'Fire',
    color: 'Yellow/Gold',
    effects: [
      'Historical Solfeggio note',
      'Transformation',
      'Increased life energy',
      'Clarity of mind',
      'Awakening imagination'
    ],
    pythagoreanReduction: '5+2+8 = 15 → 1+5 = 6',
    scientificResearch: [
      {
        study: 'Acoustic vibrations of 528Hz reduce ROS in human astrocytes',
        authors: 'Babayi-Abrari et al.',
        year: 2019,
        journal: 'Journal of Addiction and Dependence',
        finding: 'Reduced ethanol-induced cell death by approximately 20%'
      },
      {
        study: '528Hz frequency and chlorophyll molecular absorption',
        authors: 'Dr. Leonard Horowitz',
        year: 1999,
        finding: 'Chlorophyll vibrates at 528Hz, essential for oxygen/energy production'
      }
    ],
    popularName: '"Love Frequency" or "Miracle Tone"',
    harmonics: [528, 1056, 1584, 2112],
    notes: 'Most researched Solfeggio frequency. Some claims remain controversial.',
  },
  
  FA_639: {
    frequency: 639,
    noteName: 'Fa',
    originalLatin: 'Famuli tuorum',
    translation: 'Solve the pollution',
    intention: 'Connecting relationships and unity',
    chakra: 'Heart (Anahata)',
    element: 'Air',
    color: 'Green',
    effects: [
      'Harmonious relationships',
      'Enhances communication',
      'Understanding and tolerance',
      'Love and compassion',
      'Connection with others'
    ],
    pythagoreanReduction: '6+3+9 = 18 → 1+8 = 9',
    researchNotes: 'Associated with heart chakra and interpersonal harmony',
    harmonics: [639, 1278, 1917, 2556],
  },
  
  SOL_741: {
    frequency: 741,
    noteName: 'Sol',
    originalLatin: 'Solve polluti',
    translation: 'Of your wonderful acts',
    intention: 'Awakening intuition and expression',
    chakra: 'Throat (Vishuddha)',
    element: 'Ether',
    color: 'Blue',
    effects: [
      'Awakens intuition',
      'Expression and solutions',
      'Cleans cells from toxins',
      'Electromagnetic radiation protection',
      'Pure expression'
    ],
    pythagoreanReduction: '7+4+1 = 12 → 1+2 = 3',
    researchNotes: 'Historically linked to self-expression and finding truth',
    harmonics: [741, 1482, 2223, 2964],
  },
  
  LA_852: {
    frequency: 852,
    noteName: 'La',
    originalLatin: 'Labii reatum',
    translation: 'That your servants may',
    intention: 'Returning to spiritual order',
    chakra: 'Third Eye (Ajna)',
    element: 'Light',
    color: 'Indigo',
    effects: [
      'Awakens intuition',
      'Returns to spiritual order',
      'Raises awareness',
      'Opens perception',
      'Connects to higher self'
    ],
    pythagoreanReduction: '8+5+2 = 15 → 1+5 = 6',
    researchNotes: 'Associated with pineal gland activation and spiritual awareness',
    harmonics: [852, 1704, 2556, 3408],
  },
  
  // Additional frequencies discovered in later research
  
  SI_963: {
    frequency: 963,
    noteName: 'Si',
    originalLatin: 'Sancte Iohannes',
    translation: 'Saint John',
    intention: 'Divine consciousness and enlightenment',
    chakra: 'Crown (Sahasrara)',
    element: 'Consciousness',
    color: 'Violet/White',
    effects: [
      'Connection to higher dimensions',
      'Oneness and unity',
      'Divine consciousness',
      'Pure light',
      'Spiritual awakening'
    ],
    pythagoreanReduction: '9+6+3 = 18 → 1+8 = 9',
    researchNotes: 'Added in modern interpretations as crown chakra frequency',
    historicalNote: 'Later addition to complete the chakra alignment',
    harmonics: [963, 1926, 2889, 3852],
  },
  
  // Extended Solfeggio frequencies (modern additions)
  
  UT_174: {
    frequency: 174,
    noteName: 'Extended: Foundation',
    intention: 'Pain reduction and grounding',
    effects: ['Natural anesthetic', 'Security', 'Love', 'Grounding'],
    pythagoreanReduction: '1+7+4 = 12 → 1+2 = 3',
    historicalNote: 'Modern addition based on Solfeggio mathematical pattern',
  },
  
  RE_285: {
    frequency: 285,
    noteName: 'Extended: Quantum Cognition',
    intention: 'Cellular healing and regeneration',
    effects: ['Tissue regeneration', 'Immune system', 'Energy fields'],
    pythagoreanReduction: '2+8+5 = 15 → 1+5 = 6',
    historicalNote: 'Modern addition based on Solfeggio mathematical pattern',
  },
} as const;

// ============================================================================
// A=432HZ TUNING - Natural Harmonic vs A=440Hz Standard
// ============================================================================

/**
 * A=432Hz Natural Tuning
 * 
 * Historical context:
 * - Used by ancient civilizations (Egypt, Greece)
 * - Preferred by classical composers (though evidence is debated)
 * - Verdi's A (Verdi pitch): A=432Hz
 * - Changed to A=440Hz as ISO standard in 1955
 * 
 * Mathematical properties:
 * - 432 = 2^4 × 3^3 = 16 × 27
 * - 432 is divisible by 2, 3, 4, 6, 8, 9, 12, 16, 18, 24, 27, 36, 48, 54, 72, 108, 144, 216
 * - Reduces to 9: 4+3+2 = 9 (Pythagorean reduction)
 * - 9 is the number of completion in numerology
 * 
 * Harmonic relationships:
 * - C5 = 256Hz (middle C in A=432 tuning, a power of 2)
 * - A4 = 432Hz
 * - Resonates with 8Hz (alpha brain waves, Schumann resonance base)
 * 
 * Scientific research:
 * - Limited peer-reviewed studies comparing 432Hz vs 440Hz
 * - Some studies show reduced anxiety/heart rate, but sample sizes small
 * - More research needed for definitive conclusions
 * 
 * Sources:
 * - Calamassi, D. & Pomponi, G. P. (2019). "Music Tuned to 440 Hz Versus 432 Hz and the Health Effects"
 * - Jakien, K. M. & Kampel, S. D. (2017). "Auditory preferences for different musical intervals"
 */
export const A432_TUNING = {
  standard: {
    frequency: 432,
    note: 'A4',
    historicalName: 'Verdi pitch',
    mathematicalProperties: {
      factorization: '2^4 × 3^3',
      pythagoreanReduction: '4+3+2 = 9',
      divisors: [2, 3, 4, 6, 8, 9, 12, 16, 18, 24, 27, 36, 48, 54, 72, 108, 144, 216, 432],
    },
    harmonicRelations: {
      schumannResonance: '8Hz (432 = 8 × 54 = 8 × 2 × 27)',
      middleC: 256, // C4 in A=432 tuning
      alphaWaves: '8-12Hz (brain waves)',
    },
    historicalUse: [
      'Ancient Egyptian instruments',
      'Greek modes and scales',
      'Giuseppe Verdi advocacy (1884)',
      'Some Stradivarius violins',
    ],
    modernStatus: 'Alternative tuning, not ISO standard',
    isoStandard: '440Hz (since 1955)',
  },
  
  comparisonWith440: {
    difference: '8Hz (440 - 432)',
    percentDifference: '1.8%',
    musicalInterval: 'Approximately 31.77 cents (less than a semitone)',
    perceptualDifference: 'Subtle, often requires trained ear',
  },
  
  scientificStudies: [
    {
      study: 'Music tuned to 440 Hz versus 432 Hz and the health effects',
      authors: 'Calamassi & Pomponi',
      year: 2019,
      findings: 'Some participants showed decreased heart rate and anxiety with 432Hz',
      limitations: 'Small sample size, further research needed',
    },
  ],
  
  claims: [
    {
      claim: 'More harmonious and pleasing to ear',
      status: 'Subjective, individual preference',
    },
    {
      claim: 'Resonates with natural frequencies',
      status: 'Some mathematical relationships exist, causation unclear',
    },
    {
      claim: 'Healing and therapeutic effects',
      status: 'Anecdotal evidence, limited scientific validation',
    },
  ],
  
  disclaimer: 'While 432Hz has mathematical elegance and historical use, definitive scientific proof of superior healing properties remains limited. Presented for educational exploration.',
} as const;

// ============================================================================
// SCHUMANN RESONANCES - Scientifically Verified
// ============================================================================

/**
 * Schumann Resonances
 * 
 * Discovered by: Winfried Otto Schumann (1952)
 * First measured: 1960
 * 
 * These are global electromagnetic resonances in Earth's atmosphere,
 * created by lightning discharges in the cavity between Earth's surface
 * and the ionosphere.
 * 
 * Scientific basis:
 * - Wavelength ≈ Earth's circumference
 * - Fundamental frequency ≈ 7.83Hz
 * - Harmonics at ~14, 20, 26, 33Hz
 * 
 * Formula: f = c / λ, where:
 * - c = speed of light
 * - λ = wavelength ≈ Earth's circumference / mode number
 * 
 * Biological relevance:
 * - Overlaps with human alpha brain wave range (8-12Hz)
 * - Some research suggests human circadian rhythms may synchronize
 * 
 * Sources:
 * - Schumann, W. O. (1952). "Über die strahlungslosen Eigenschwingungen"
 * - König, H. L. (1974). "ELF and VLF signal properties"
 * - NASA monitoring of Schumann resonances
 */
export const SCHUMANN_RESONANCES = {
  fundamental: {
    frequency: 7.83,
    mode: 1,
    discoveredBy: 'Winfried Otto Schumann',
    year: 1952,
    scientificBasis: 'Earth-ionosphere cavity resonance',
    wavelength: 'Earth circumference ≈ 40,000 km',
    formula: 'f ≈ c / (2πR) where R = Earth radius',
  },
  
  harmonics: [
    { mode: 1, frequency: 7.83, name: 'Fundamental' },
    { mode: 2, frequency: 14.3, name: '2nd harmonic' },
    { mode: 3, frequency: 20.8, name: '3rd harmonic' },
    { mode: 4, frequency: 27.3, name: '4th harmonic' },
    { mode: 5, frequency: 33.8, name: '5th harmonic' },
  ],
  
  biologicalCorrelation: {
    brainWaves: {
      delta: '0.5-4Hz (deep sleep)',
      theta: '4-8Hz (meditation, creativity)',
      alpha: '8-12Hz (relaxed awareness) - OVERLAPS with Schumann',
      beta: '12-30Hz (active thinking)',
      gamma: '30-100Hz (peak concentration)',
    },
    research: [
      {
        finding: 'Alpha brain waves (8-12Hz) overlap with Schumann fundamental',
        implication: 'Possible evolutionary synchronization mechanism',
        status: 'Hypothesis with supporting evidence, not definitively proven',
      },
    ],
  },
  
  variations: {
    dailyCycle: 'Varies slightly with time of day',
    seasonalChanges: 'Affected by solar activity',
    lightningActivity: 'Increases with global thunderstorm activity',
  },
  
  measurement: {
    method: 'ELF receivers, magnetometers',
    locations: 'Multiple monitoring stations worldwide',
    dataSource: 'NASA, NOAA, university research stations',
  },
  
  scientificConsensus: 'Well-established natural phenomenon, monitored and studied extensively',
} as const;

// ============================================================================
// CHAKRA FREQUENCY MAPPINGS - Traditional & Modern Interpretations
// ============================================================================

/**
 * Chakra Frequencies
 * 
 * NOTE: There is NO ancient traditional text that specifies exact Hz frequencies
 * for chakras. The Sanskrit texts describe chakras in terms of:
 * - Colors (not exact wavelengths)
 * - Bija mantras (seed sounds)
 * - Element associations
 * - Petal counts
 * 
 * Modern Hz frequencies are 20th-century interpretations attempting to map:
 * 1. Solfeggio frequencies to chakras
 * 2. Color wavelength frequencies
 * 3. Octave-based harmonic systems
 * 
 * Different systems use different mappings. This is ONE interpretation.
 * 
 * Traditional sources:
 * - Sat-Cakra-Nirupana (1577) - Sanskrit text on chakras
 * - Yoga Upanishads (pre-1000 CE)
 * - Tantric texts
 * 
 * Modern frequency mappings:
 * - Solfeggio correspondence (20th century)
 * - Color wavelength calculations
 * - Intuitive/channeled information
 */
export const CHAKRA_SYSTEM = {
  muladhara: {
    name: 'Muladhara',
    english: 'Root Chakra',
    sanskrit: 'मूलाधार (Mūlādhāra)',
    meaning: '"Root support" or "Foundation"',
    frequency: 396, // Solfeggio UT
    alternativeFrequencies: [256, 194.18], // Different systems use different frequencies
    location: 'Base of spine, perineum',
    element: 'Earth (Prithvi)',
    color: 'Red',
    colorWavelength: '620-750nm',
    bijaMantra: 'LAM (लं)',
    petals: 4,
    petalsSymbolism: '4 nadis (energy channels)',
    yantra: 'Yellow square (earth)',
    deity: 'Ganesha (remover of obstacles)',
    goddess: 'Dakini',
    animalSymbol: 'Elephant (Airavata)',
    qualities: [
      'Survival',
      'Grounding',
      'Stability',
      'Security',
      'Basic needs',
    ],
    imbalanceSymptoms: {
      excess: 'Greed, materialism, resistance to change',
      deficient: 'Fear, anxiety, disconnection from body',
    },
    balancingPractices: [
      'Grounding exercises',
      'Walking barefoot',
      'Root vegetable diet',
      'Yoga: Mountain pose, Warrior poses',
    ],
    traditionalSource: 'Sat-Cakra-Nirupana (1577)',
  },
  
  svadhisthana: {
    name: 'Svadhisthana',
    english: 'Sacral Chakra',
    sanskrit: 'स्वाधिष्ठान (Svādhiṣṭhāna)',
    meaning: '"One\'s own abode"',
    frequency: 417, // Solfeggio RE
    alternativeFrequencies: [288, 210.42],
    location: 'Lower abdomen, sacrum, reproductive organs',
    element: 'Water (Apas)',
    color: 'Orange',
    colorWavelength: '590-620nm',
    bijaMantra: 'VAM (वं)',
    petals: 6,
    petalsSymbolism: '6 negative qualities to overcome',
    yantra: 'Silver crescent moon (water)',
    deity: 'Brahma (creator)',
    goddess: 'Rakini',
    animalSymbol: 'Crocodile or fish',
    qualities: [
      'Creativity',
      'Sexuality',
      'Emotions',
      'Pleasure',
      'Relationships',
    ],
    imbalanceSymptoms: {
      excess: 'Addiction, emotional volatility, codependency',
      deficient: 'Rigidity, emotional numbness, fear of pleasure',
    },
    balancingPractices: [
      'Creative expression',
      'Dance and movement',
      'Water activities',
      'Yoga: Hip openers, Goddess pose',
    ],
    traditionalSource: 'Sat-Cakra-Nirupana (1577)',
  },
  
  manipura: {
    name: 'Manipura',
    english: 'Solar Plexus Chakra',
    sanskrit: 'मणिपुर (Maṇipūra)',
    meaning: '"City of jewels"',
    frequency: 528, // Solfeggio MI
    alternativeFrequencies: [320, 126.22],
    location: 'Upper abdomen, solar plexus',
    element: 'Fire (Agni)',
    color: 'Yellow',
    colorWavelength: '570-590nm',
    bijaMantra: 'RAM (रं)',
    petals: 10,
    petalsSymbolism: '10 pranas (vital airs)',
    yantra: 'Red downward-pointing triangle (fire)',
    deity: 'Rudra (destroyer of ignorance)',
    goddess: 'Lakini',
    animalSymbol: 'Ram',
    qualities: [
      'Personal power',
      'Will',
      'Confidence',
      'Transformation',
      'Metabolism',
    ],
    imbalanceSymptoms: {
      excess: 'Domineering, aggressive, perfectionist',
      deficient: 'Low self-esteem, victim mentality, passive',
    },
    balancingPractices: [
      'Core strengthening',
      'Sun exposure',
      'Goal setting',
      'Yoga: Boat pose, Warrior III',
    ],
    traditionalSource: 'Sat-Cakra-Nirupana (1577)',
  },
  
  anahata: {
    name: 'Anahata',
    english: 'Heart Chakra',
    sanskrit: 'अनाहत (Anāhata)',
    meaning: '"Unstruck" or "Unbeaten"',
    frequency: 639, // Solfeggio FA
    alternativeFrequencies: [341.3, 136.10],
    location: 'Center of chest, heart',
    element: 'Air (Vayu)',
    color: 'Green',
    colorWavelength: '495-570nm',
    bijaMantra: 'YAM (यं)',
    petals: 12,
    petalsSymbolism: '12 divine qualities',
    yantra: 'Smoke-colored hexagram (two triangles)',
    deity: 'Isha (lord)',
    goddess: 'Kakini',
    animalSymbol: 'Antelope or deer',
    qualities: [
      'Love',
      'Compassion',
      'Forgiveness',
      'Empathy',
      'Connection',
    ],
    imbalanceSymptoms: {
      excess: 'Codependency, jealousy, giving too much',
      deficient: 'Loneliness, isolation, inability to trust',
    },
    balancingPractices: [
      'Loving-kindness meditation',
      'Heart-opening yoga',
      'Gratitude practice',
      'Yoga: Camel pose, Fish pose',
    ],
    traditionalSource: 'Sat-Cakra-Nirupana (1577)',
    specialNote: '"Anahata Nada" refers to the unstruck sound heard in deep meditation',
  },
  
  vishuddha: {
    name: 'Vishuddha',
    english: 'Throat Chakra',
    sanskrit: 'विशुद्ध (Viśuddha)',
    meaning: '"Especially pure" or "Purification"',
    frequency: 741, // Solfeggio SOL
    alternativeFrequencies: [384, 141.27],
    location: 'Throat, thyroid',
    element: 'Ether/Space (Akasha)',
    color: 'Blue',
    colorWavelength: '450-495nm',
    bijaMantra: 'HAM (हं)',
    petals: 16,
    petalsSymbolism: '16 Sanskrit vowels',
    yantra: 'White circle or crescent (ether/space)',
    deity: 'Sadashiva (eternal Shiva)',
    goddess: 'Shakini',
    animalSymbol: 'White elephant',
    qualities: [
      'Communication',
      'Expression',
      'Truth',
      'Creativity',
      'Authenticity',
    ],
    imbalanceSymptoms: {
      excess: 'Talking too much, gossip, inability to listen',
      deficient: 'Fear of speaking, difficulty expressing, lying',
    },
    balancingPractices: [
      'Singing and chanting',
      'Authentic communication',
      'Journaling',
      'Yoga: Shoulder stand, Fish pose',
    ],
    traditionalSource: 'Sat-Cakra-Nirupana (1577)',
  },
  
  ajna: {
    name: 'Ajna',
    english: 'Third Eye Chakra',
    sanskrit: 'आज्ञा (Ājñā)',
    meaning: '"Command" or "Perceive"',
    frequency: 852, // Solfeggio LA
    alternativeFrequencies: [426.7, 221.23],
    location: 'Between eyebrows, pineal gland',
    element: 'Light (Manas - mind)',
    color: 'Indigo',
    colorWavelength: '420-450nm',
    bijaMantra: 'OM (ॐ)',
    petals: 2,
    petalsSymbolism: '2 main nadis: Ida (left) and Pingala (right)',
    yantra: 'Circle with downward-pointing triangle',
    deity: 'Ardhanarishvara (Shiva-Shakti union)',
    goddess: 'Hakini',
    animalSymbol: 'None (beyond animal nature)',
    qualities: [
      'Intuition',
      'Insight',
      'Vision',
      'Imagination',
      'Wisdom',
    ],
    imbalanceSymptoms: {
      excess: 'Delusion, hallucination, obsession with psychic phenomena',
      deficient: 'Poor memory, lack of imagination, denial',
    },
    balancingPractices: [
      'Meditation',
      'Visualization',
      'Dream work',
      'Yoga: Child\'s pose, Forward folds',
    ],
    traditionalSource: 'Sat-Cakra-Nirupana (1577)',
    pinealGland: 'Associated with melatonin production and circadian rhythms',
  },
  
  sahasrara: {
    name: 'Sahasrara',
    english: 'Crown Chakra',
    sanskrit: 'सहस्रार (Sahasrāra)',
    meaning: '"Thousand-petaled"',
    frequency: 963, // Extended Solfeggio SI
    alternativeFrequencies: [480, 172.06],
    location: 'Top of head, above crown',
    element: 'Thought/Consciousness',
    color: 'Violet or White',
    colorWavelength: '380-450nm (violet) or all wavelengths (white)',
    bijaMantra: 'AH (अः) or silence',
    petals: 1000,
    petalsSymbolism: 'Infinite - represents complete enlightenment',
    yantra: 'Full moon or thousand-petaled lotus',
    deity: 'None (pure consciousness)',
    goddess: 'None (beyond form)',
    animalSymbol: 'None (transcends animal realm)',
    qualities: [
      'Unity consciousness',
      'Enlightenment',
      'Spirituality',
      'Transcendence',
      'Connection to Divine',
    ],
    imbalanceSymptoms: {
      excess: 'Spiritual addiction, disassociation from body',
      deficient: 'Materialism, cynicism, disconnection from spirit',
    },
    balancingPractices: [
      'Meditation on consciousness',
      'Prayer',
      'Silence',
      'Yoga: Headstand, Lotus pose',
    ],
    traditionalSource: 'Sat-Cakra-Nirupana (1577)',
    specialNote: 'Some traditions say crown chakra has no fixed location, exists above physical body',
  },
} as const;

// IMPORTANT DISCLAIMER
export const FREQUENCY_DISCLAIMER = {
  scientificValidation: 'Varies by frequency system',
  solfeggio: {
    historicalBasis: 'Strong - documented in historical musical texts',
    scientificValidation: 'Limited - some studies on 528Hz, more research needed',
    culturalSignificance: 'High - used in sacred music for centuries',
  },
  chakras: {
    traditionalBasis: 'Ancient texts describe chakras but do NOT specify Hz frequencies',
    modernFrequencies: '20th-century interpretations, not from Sanskrit sources',
    validityNote: 'No consensus on "correct" frequencies - multiple systems exist',
  },
  a432tuning: {
    historicalBasis: 'Some evidence of historical use',
    scientificValidation: 'Very limited - few rigorous studies',
    subjectivity: 'Preference varies between individuals',
  },
  schumannResonance: {
    scientificValidation: 'Strong - well-established, measurable phenomenon',
    biologicalEffects: 'Hypothesis with some supporting evidence',
  },
  generalNote: 'Sound healing has long historical use and anecdotal support. However, many specific claims about individual frequencies lack rigorous scientific validation. This information is presented for educational exploration and should not replace medical advice.',
} as const;

export default {
  SOLFEGGIO_FREQUENCIES,
  A432_TUNING,
  SCHUMANN_RESONANCES,
  CHAKRA_SYSTEM,
  FREQUENCY_DISCLAIMER,
};

